import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL, IMAGES } from "@/data/content";
import { MaskedLines } from "./anim";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-cream">
      <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 pt-32 pb-16 md:pt-40 md:pb-24 flex flex-col">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-xs tracking-[0.25em] uppercase text-ink-soft mb-6"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif font-light text-ink text-[3.25rem] leading-[0.95] sm:text-7xl lg:text-[7.5rem] tracking-tight max-w-[15ch]">
          <MaskedLines lines={t.hero.title} delay={0.4} />
        </h1>

        {/* Home image between title and description */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.9 }}
          className="mt-10 w-full max-w-4xl overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(45,42,38,0.15)]"
          data-testid="hero-image"
        >
          <motion.img
            style={{ y: imgY, scale: 1.12 }}
            src={IMAGES.homeTop}
            alt="Steve en séance de massage à domicile"
            className="w-full h-[260px] sm:h-[360px] md:h-[460px] object-cover object-center"
          />
        </motion.div>

        <div className="mt-10 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="font-light text-base sm:text-lg leading-relaxed text-ink-soft max-w-xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.a
            data-testid="hero-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-8 py-4 text-xs tracking-[0.18em] uppercase hover:bg-brand transition-colors duration-500 whitespace-nowrap w-fit"
          >
            {t.hero.cta}
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
